import React, { useMemo, useState } from 'react';
import { Button, Carousel, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { Thumbnail, MarkdownDisplay, classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faImage } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { ConfiguratorPresetDto, ConfiguratorPresetGroupDto } from '@/services/configurator';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { useOptionConfigurator } from '../../OptionConfiguratorContext';
import { useConfiguratorMutations } from '../../ConfiguratorMutationContext';
import { useImageUrls } from '../../useImageUrls';
import styles from './StyleSelectionCard.module.scss';

export interface StyleSelectionCardProps {
    style: ConfiguratorPresetDto;
    styleGroup: ConfiguratorPresetGroupDto;
    /** Whether this style is the group's current selection (optimistic — owned by the modal). */
    isSelected: boolean;
    /** Notify the modal that this style was picked, so it can reflect it optimistically. */
    onPicked: (styleId: number) => void;
}

export const StyleSelectionCard: React.FC<StyleSelectionCardProps> = ({
    style,
    styleGroup,
    isSelected,
    onPicked,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const { viewOnly, isShowroom } = useOptionConfigurator();
    const { getOptionPresetImageUrl } = useImageUrls();
    const { pickStyle, isPickingStyle } = useConfiguratorMutations();

    // Room-scoped groups pick with their roomId; global/Main groups pick globally (deadline endpoint).
    const pickRoomId = styleGroup.roomId ?? undefined;
    const isPending = isPickingStyle;
    // Locked = options sent or deadline expired; irrelevant in the anonymous showroom.
    const isLocked = styleGroup.isLocked && !isShowroom;

    const sortedImages = useMemo(
        () => [...style.images].sort((a, b) => a.sortOrder - b.sortOrder),
        [style.images]
    );

    // Selecting a style just picks it and leaves the dialog open — accepting / proceeding is a
    // separate action handled by the modal footer. There's no unselect: a group always keeps a
    // selection, and picking another style replaces it.
    const handleAccept = () => {
        onPicked(style.id);
        pickStyle(style.id, pickRoomId);
    };

    // Clicking anywhere on the card selects the style (a convenience alongside the Select button).
    // Excluded: the actions row (More details / Select), which stops propagation below. Disabled when
    // the style is already selected, in view-only, or the group is locked.
    const canSelect = !isSelected && !viewOnly && !isLocked;
    const handleCardClick = () => {
        if (canSelect) handleAccept();
    };

    return (
        <div
            className={classnames(styles.card, { [styles.selected]: isSelected, [styles.clickable]: canSelect })}
            onClick={handleCardClick}>
            {isSelected && (
                <div className={styles.checkBadge}>
                    <FontAwesomeIcon icon={faCheck} />
                </div>
            )}
            <div className={styles.media}>
                {showDetails ? (
                    <div className={styles.detailsText}>
                        <MarkdownDisplay markdown={style.description} />
                    </div>
                ) : sortedImages.length > 1 ? (
                    <Carousel
                        mode="indicators"
                        enableLightbox={false}
                        className={styles.carousel}
                        height={220}
                        src={sortedImages.map((image) => ({
                            type: 'image',
                            src: getOptionPresetImageUrl(image.id),
                            thumbnailSrc: getOptionPresetImageUrl(image.id),
                            title: style.name,
                        }))}
                    />
                ) : sortedImages[0] ? (
                    <Thumbnail
                        className={styles.image}
                        src={getOptionPresetImageUrl(sortedImages[0].id)}
                        alt={style.name}
                    />
                ) : (
                    <div className={styles.imageFallback}>
                        <FontAwesomeIcon icon={faImage} size="lg" />
                    </div>
                )}
            </div>

            <div className={styles.body}>
                <div className={styles.header}>
                    <h4 className={styles.name}>{style.name}</h4>
                    <BuyerCurrencyDisplay value={style.price} />
                </div>

                {/* One-line description teaser, always shown (in both image and details modes) so
                    toggling "More details" doesn't shift the card's layout. The full description is
                    revealed in the media area above when details are open. */}
                <div className={styles.teaser}>
                    <MarkdownDisplay markdown={style.description} />
                </div>

                {/* Stop card-click selection from firing for the action buttons (More details / Select);
                    each keeps its own behaviour. */}
                <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
                    <Button variant="tertiary" size="sm" onClick={() => setShowDetails((v) => !v)}>
                        {showDetails ? <Trans>Back</Trans> : <Trans>More details</Trans>}
                    </Button>
                    <SelectButton
                        isSelected={isSelected}
                        onSelect={handleAccept}
                        isPending={isPending}
                        viewOnly={viewOnly}
                        isLocked={isLocked}
                    />
                </div>
            </div>
        </div>
    );
};

interface SelectButtonProps {
    isSelected: boolean;
    onSelect: () => void;
    isPending: boolean;
    viewOnly: boolean;
    /** The style group has been signed/locked; the server rejects picks. */
    isLocked: boolean;
}

// There's no unselect — a group always keeps a selection; a selected style just shows "Selected".
const SelectButton: React.FC<SelectButtonProps> = ({ isSelected, onSelect, isPending, viewOnly, isLocked }) => {
    if (isSelected) {
        return (
            <Button variant="tertiary" size="sm" disabled icon={faCheck} iconPosition="right">
                <Trans>Selected</Trans>
            </Button>
        );
    }

    if (isLocked) {
        return (
            <SimpleTooltip content={<Trans>This style group is locked</Trans>}>
                <Button variant="primary" size="sm" disabled>
                    <Trans>Select</Trans>
                </Button>
            </SimpleTooltip>
        );
    }

    return (
        <SimpleTooltip disabled={!viewOnly} content={<Trans>Selections are disabled in this view</Trans>}>
            <Button variant="primary" size="sm" onClick={onSelect} loading={isPending} disabled={viewOnly}>
                <Trans>Select</Trans>
            </Button>
        </SimpleTooltip>
    );
};
